import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Logo from '../../components/Logo';
import {
  CheckCircle,
  XCircle,
  Trophy,
  BarChart3,
  RefreshCw,
  Award,
  FileText
} from 'lucide-react';

const AssessmentComplete = () => {
  const { token } = useParams();
  const location = useLocation();
  const [result, setResult] = useState(location.state?.result || null);
  const [loading, setLoading] = useState(!result);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (!result) {
      fetchResult();
    }
  }, [token, result]);

  const fetchResult = async () => {
    try {
      const response = await axios.get(`/api/student/${token}/results`);
      const latestResult = response.data[0]; // Get the most recent result
      setResult(latestResult);
    } catch (error) {
      console.error('Failed to fetch result:', error);
    } finally {
      setLoading(false);
    }
  };

  const getResultIcon = () => {
    if (result?.passed) {
      return <Trophy size={64} className="result-icon success" />;
    }
    return <XCircle size={64} className="result-icon failed" />;
  };

  const getResultMessage = () => {
    if (result?.passed) {
      return {
        title: "Congratulations!",
        subtitle: "You have successfully passed the Distance Education Readiness Assessment",
        message: "You are ready to begin your distance education journey with Intellectual Point."
      };
    }
    return {
      title: "Assessment Complete",
      subtitle: "You did not meet the passing requirements this time",
      message: "Don't worry! You can retake the assessment immediately to improve your score."
    };
  };

  if (loading) {
    return (
      <div className="complete-container">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  const { title, subtitle, message } = getResultMessage();

  return (
    <div className="complete-container">
      <div className="container">
        <div className="complete-content">
          <div className="complete-header">
            <Logo size="large" />
            <h1>Distance Education Readiness Assessment</h1>
          </div>

          <motion.div
            className="result-card"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="result-header">
              {getResultIcon()}
              <h2 className={`result-title ${result?.passed ? 'success' : 'failed'}`}>
                {title}
              </h2>
              <p className="result-subtitle">{subtitle}</p>
            </div>

            <div className="result-details">
              <div className="score-display">
                <div className={`main-score ${result?.passed ? 'passed' : 'failed'}`}>
                  <span className="score-number">{result?.percentage}%</span>
                  <span className="score-text">Final Score</span>
                </div>

                <div className="score-breakdown">
                  <div className="score-item">
                    <BarChart3 size={20} />
                    <span>{result?.score} / {result?.totalPoints} points</span>
                  </div>
                  <div className="score-item">
                    <Award size={20} />
                    <span>Attempt #{result?.attemptNumber}</span>
                  </div>
                  {result?.assessmentCode && (
                    <div className="score-item assessment-code-item">
                      <FileText size={20} />
                      <span>Code: {result.assessmentCode}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="result-stats">
                <div className="stat-grid">
                  <div className="stat-item">
                    <div className="stat-icon">
                      <CheckCircle size={24} />
                    </div>
                    <div className="stat-content">
                      <h4>Status</h4>
                      <p className={result?.passed ? 'text-success' : 'text-danger'}>
                        {result?.passed ? 'Passed' : 'Failed'}
                      </p>
                    </div>
                  </div>

                  <div className="stat-item">
                    <div className="stat-icon">
                      <BarChart3 size={24} />
                    </div>
                    <div className="stat-content">
                      <h4>Required</h4>
                      <p>70% to pass</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="result-message">
              <p>{message}</p>
            </div>

            <div className="result-actions">
              {!result?.passed && result?.canRetake && (
                <Link
                  to={`/assessment/${token}`}
                  className="btn btn-primary btn-lg"
                >
                  <RefreshCw size={20} />
                  Retake Assessment
                </Link>
              )}
            </div>

            {result?.passed && (
              <motion.div
                className="success-animation"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <div className="confetti">
                  {[...Array(20)].map((_, i) => (
                    <div key={i} className={`confetti-piece confetti-${i % 4}`} />
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>

          <div className="next-steps">
            <h3>What's Next?</h3>
            {result?.passed ? (
              <div className="steps-content">
                <div className="step-item">
                  <CheckCircle size={20} />
                  <span>You will receive a confirmation email with your results</span>
                </div>
                <div className="step-item">
                  <CheckCircle size={20} />
                  <span>Our admissions team will contact you regarding next steps</span>
                </div>
                <div className="step-item">
                  <CheckCircle size={20} />
                  <span>Prepare for your distance education journey!</span>
                </div>
              </div>
            ) : (
              <div className="steps-content">
                <div className="step-item">
                  <RefreshCw size={20} />
                  <span>Review areas where you need improvement</span>
                </div>
                <div className="step-item">
                  <RefreshCw size={20} />
                  <span>Practice your computer skills and technical knowledge</span>
                </div>
                <div className="step-item">
                  <RefreshCw size={20} />
                  <span>Retake the assessment when you feel ready</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .complete-container {
          min-height: 100vh;
          background: linear-gradient(135deg, var(--primary-bg) 0%, #d6e7ff 100%);
          padding: 40px 20px;
        }

        .complete-content {
          max-width: 800px;
          margin: 0 auto;
        }

        .complete-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .complete-header h1 {
          font-size: 28px;
          color: var(--primary-accent);
          margin-top: 24px;
          font-weight: 700;
        }

        .result-card {
          background: var(--white);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-lg);
          padding: 40px;
          margin-bottom: 32px;
          position: relative;
          overflow: hidden;
        }

        .result-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .result-icon {
          margin-bottom: 16px;
        }

        .result-icon.success {
          color: var(--success);
        }

        .result-icon.failed {
          color: var(--danger);
        }

        .result-title {
          font-size: 32px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .result-title.success {
          color: var(--success);
        }

        .result-title.failed {
          color: var(--danger);
        }

        .result-subtitle {
          font-size: 18px;
          color: var(--text-secondary);
          margin: 0;
        }

        .result-details {
          margin-bottom: 32px;
        }

        .score-display {
          text-align: center;
          margin-bottom: 32px;
        }

        .main-score {
          display: inline-block;
          padding: 24px 32px;
          border-radius: var(--border-radius-lg);
          margin-bottom: 20px;
        }

        .main-score.passed {
          background: linear-gradient(135deg, rgba(40, 167, 69, 0.1), rgba(40, 167, 69, 0.2));
          border: 2px solid var(--success);
        }

        .main-score.failed {
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.1), rgba(220, 53, 69, 0.2));
          border: 2px solid var(--danger);
        }

        .score-number {
          display: block;
          font-size: 48px;
          font-weight: 900;
          line-height: 1;
          margin-bottom: 8px;
        }

        .main-score.passed .score-number {
          color: var(--success);
        }

        .main-score.failed .score-number {
          color: var(--danger);
        }

        .score-text {
          display: block;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-secondary);
        }

        .score-breakdown {
          display: flex;
          justify-content: center;
          gap: 32px;
        }

        .score-item {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--text-secondary);
          font-weight: 500;
        }

        .assessment-code-item span {
          font-family: 'Courier New', monospace;
          font-weight: 700;
          color: var(--primary-accent);
          background: var(--white);
          padding: 4px 8px;
          border-radius: 4px;
          border: 1px solid var(--primary-accent);
        }

        .result-stats {
          background-color: var(--primary-bg);
          border-radius: var(--border-radius);
          padding: 24px;
        }

        .stat-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 24px;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          background-color: var(--white);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary-accent);
        }

        .stat-content h4 {
          margin: 0 0 4px 0;
          font-size: 14px;
          color: var(--text-secondary);
          text-transform: uppercase;
          font-weight: 600;
        }

        .stat-content p {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .result-message {
          text-align: center;
          margin-bottom: 32px;
          padding: 20px;
          background-color: var(--gray-100);
          border-radius: var(--border-radius);
        }

        .result-message p {
          margin: 0;
          font-size: 16px;
          color: var(--text-primary);
          line-height: 1.6;
        }

        .result-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .success-animation {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          overflow: hidden;
        }

        .confetti {
          position: absolute;
          width: 100%;
          height: 100%;
        }

        .confetti-piece {
          position: absolute;
          width: 10px;
          height: 10px;
          animation: confetti-fall 3s linear infinite;
        }

        .confetti-0 {
          background-color: var(--success);
          left: 10%;
          animation-delay: 0s;
        }

        .confetti-1 {
          background-color: var(--primary-accent);
          left: 20%;
          animation-delay: 0.5s;
        }

        .confetti-2 {
          background-color: var(--warning);
          left: 30%;
          animation-delay: 1s;
        }

        .confetti-3 {
          background-color: var(--info);
          left: 40%;
          animation-delay: 1.5s;
        }

        @keyframes confetti-fall {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }

        .next-steps {
          background: var(--white);
          border-radius: var(--border-radius-lg);
          padding: 32px;
          box-shadow: var(--shadow-sm);
        }

        .next-steps h3 {
          text-align: center;
          color: var(--primary-accent);
          margin-bottom: 24px;
          font-size: 24px;
        }

        .steps-content {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .step-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background-color: var(--primary-bg);
          border-radius: var(--border-radius);
          color: var(--text-primary);
        }

        @media (max-width: 768px) {
          .result-card {
            padding: 24px;
          }

          .complete-header h1 {
            font-size: 24px;
          }

          .result-title {
            font-size: 28px;
          }

          .score-number {
            font-size: 36px;
          }

          .score-breakdown {
            flex-direction: column;
            gap: 16px;
            align-items: center;
          }

          .stat-grid {
            grid-template-columns: 1fr;
          }

          .result-actions {
            flex-direction: column;
            align-items: center;
          }

          .result-actions .btn {
            width: 100%;
            max-width: 280px;
          }

          .next-steps {
            padding: 24px;
          }
        }
      `}</style>
    </div>
  );
};

export default AssessmentComplete;
